/**
 * Team Service
 * Real API implementation for AI Labs Team Library
 */

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://z6zx064ldk.execute-api.us-east-1.amazonaws.com/prod';

/**
 * 현재 로그인한 사용자 정보 가져오기
 */
const getCurrentUserInfo = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo');
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr);
      return {
        id: userInfo.userId || userInfo.sub || null,
        name: userInfo.name || userInfo.username || '사용자',
        department: userInfo.department || ''
      };
    }
  } catch (error) {
    console.warn('[TeamService] Failed to get user info:', error);
  }
  // 환경변수 fallback (개발용)
  return {
    id: import.meta.env.VITE_USER_ID || null,
    name: '사용자',
    department: ''
  };
};

/**
 * API fetch wrapper with error handling
 */
const apiFetch = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  const currentUser = getCurrentUserInfo();

  const defaultHeaders = {
    'Content-Type': 'application/json',
    ...(currentUser.id && { 'x-user-id': currentUser.id })
  };

  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `API Error: ${response.status}`);
    }

    return data;
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error);
    throw error;
  }
};

export const teamService = {
  /**
   * Get current user info
   * @returns {Object}
   */
  getCurrentUser() {
    return getCurrentUserInfo();
  },

  /**
   * Get all team works
   * @param {string} filter - Filter by workType (all, table, document, search)
   * @returns {Promise<Array>}
   */
  async getWorks(filter = 'all') {
    try {
      const queryParams = filter !== 'all' ? `?workType=${filter}` : '';
      const result = await apiFetch(`/team/works${queryParams}`);
      return result.data?.works || [];
    } catch (error) {
      console.error('Failed to get works:', error);
      return [];
    }
  },

  /**
   * Get a single work by ID
   * @param {string} id - Work ID
   * @returns {Promise<Object|null>}
   */
  async getWorkById(id) {
    try {
      const result = await apiFetch(`/team/works/${id}`);
      return result.data?.work || null;
    } catch (error) {
      console.error('Failed to get work:', error);
      return null;
    }
  },

  /**
   * Share a work to team library
   * @param {Object} data - Work data
   * @returns {Promise<Object>}
   */
  async shareWork(data) {
    const currentUser = getCurrentUserInfo();
    const result = await apiFetch('/team/works', {
      method: 'POST',
      body: JSON.stringify({
        title: data.title,
        description: data.description || '',
        content: data.content,
        workType: data.workType || 'table',
        table: data.table || null,
        tags: data.tags || [],
        userName: currentUser.name,
        department: currentUser.department
      })
    });
    return result.data?.work || result.data;
  },

  /**
   * Toggle like on a work
   * @param {string} workId - Work ID
   * @returns {Promise<Object>} Updated likes info
   */
  async toggleLike(workId) {
    try {
      const result = await apiFetch(`/team/works/${workId}/like`, {
        method: 'POST'
      });
      return result.data || { likes: 0, isLiked: false };
    } catch (error) {
      console.error('Failed to toggle like:', error);
      throw error;
    }
  },

  /**
   * Check if current user liked a work
   * @param {string} workId - Work ID
   * @returns {Promise<boolean>}
   */
  async isLiked(workId) {
    try {
      const work = await this.getWorkById(workId);
      if (!work) return false;
      const currentUser = getCurrentUserInfo();
      return currentUser.id && (work.likedBy || []).includes(currentUser.id);
    } catch (error) {
      return false;
    }
  },

  /**
   * Get comments for a work
   * @param {string} workId - Work ID
   * @returns {Promise<Array>}
   */
  async getComments(workId) {
    try {
      const result = await apiFetch(`/team/works/${workId}/comments`);
      return result.data?.comments || [];
    } catch (error) {
      console.error('Failed to get comments:', error);
      return [];
    }
  },

  /**
   * Add a comment to a work
   * @param {string} workId - Work ID
   * @param {string} content - Comment content
   * @returns {Promise<Object>} New comment
   */
  async addComment(workId, content) {
    const currentUser = getCurrentUserInfo();
    const result = await apiFetch(`/team/works/${workId}/comments`, {
      method: 'POST',
      body: JSON.stringify({
        content,
        userName: currentUser.name
      })
    });
    return result.data?.comment || result.data;
  },

  /**
   * Delete a comment
   * @param {string} workId - Work ID
   * @param {string} commentId - Comment ID
   * @returns {Promise<boolean>}
   */
  async deleteComment(workId, commentId) {
    const result = await apiFetch(`/team/works/${workId}/comments/${commentId}`, {
      method: 'DELETE'
    });
    return result.success || false;
  },

  /**
   * Delete a work
   * @param {string} workId - Work ID
   * @returns {Promise<boolean>}
   */
  async deleteWork(workId) {
    const result = await apiFetch(`/team/works/${workId}`, {
      method: 'DELETE'
    });
    return result.success || false;
  },

  /**
   * Get user ID
   * @returns {string|null}
   */
  getUserId() {
    return getCurrentUserInfo().id;
  }
};

export default teamService;
